/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import React from 'react';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { withNamespaces } from 'react-i18next';
import { Container, Row, Column } from '../../../styles/Grid';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import Spacer from '../../ui/Spacer';
import { MediaQueries } from '../../../styles/Grid';
import DotComponent from '../../ui/DotComponent';
import GalleryCarousel from '../../ui/GalleryCarousel';
import { formatStringToId } from '../../../utils/stringToId';
import { getshowStyles } from '../../../utils/responsive';
import { SolutionsInTheAreaKeys } from '../../../utils/dictionarykeys';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { componentHasStickyLabel } from '../StickyBar';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import CtaLink from '../../ui/CtaLink';
import applyPluralRule from '../../../utils/pluralRule';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import { arrows } from '../../ui/CtaLink/types';
import LinkWrapper from '../../ui/LinkWrapper';
import { setDataLayerObj } from '../../../utils/analytics';
import * as styles from './index.styles';

export const defaultData = {
  SolutionsHeadline: { value: '' },
  BodyText: {
    value: '',
  },
  QuantityOfLocations: null,
  QuantityOfOfficesToRent: null,
  QuantityOfCoworkingDesks: null,
  QuantityOfOffices: null,
  QuantityOfMeetingRooms: null,
  Images: { value: [] },
  CTALink: {
    value: {
      href: '',
      text: '',
    },
  },
  ShowHardSellCTA: { value: false },
  ShowSoftCTA: { value: false },
};

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 404,
    height: 539,
    gravity: 'auto:subject',
    aspectRatio: '3:4',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 404,
    height: 539,
    gravity: 'auto:subject',
    aspectRatio: '3:4',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 327,
    height: 436,
    gravity: 'auto:subject',
    aspectRatio: '3:4',
  },
  [Breakpoints.sm]: {
    hidden: true,
  },
};

export class SolutionsInTheArea extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  constructor(props) {
    super(props);

    const { fields } = this;
    const { t } = props;
    const contextFields = get(props, 'sitecoreContext.route.fields', {});
    const componentName = get(props, 'rendering.componentName');
    const wildcards = getWildcardData(this.props.sitecoreContext);

    this.wildcardValues = {
      componentName,
      contextFields,
      fields,
      t,
      wildcards,
    };
  }

  renderStat = (statValue, statLabel) => {
    if (statValue.value <= 0) return null;

    const label = applyPluralRule({
      quantity: statValue.value,
      dictionary: statLabel,
    });

    return (
      <Column
        css={styles.statItem}
        key={formatStringToId(statLabel)}
        cols={{ sm: [3, 6], md: [2, 4], lg: [2, 5] }}
      >
        <p css={styles.statValue}>
          <Text field={statValue} editable={false} />
        </p>
        <p css={styles.statLabel}>{label}</p>
      </Column>
    );
  };

  getViewAllCTAField = () => {
    const { fields, wildcardValues } = this;
    const { t } = this.props;
    return {
      value: {
        href: get(fields, 'CTALink.value', ''),
        text: transformWildcardsToText({
          ...wildcardValues,
          text: t(SolutionsInTheAreaKeys.ViewAllLocationsCTA),
        }),
      },
    };
  };

  createLinkAnalyticsHandler = id => () => {
    const { href, text } = this.getViewAllCTAField().value;
    setDataLayerObj({
      event: 'ctaClick',
      buttonId: id,
      buttonName: text,
      ctaTarget: href,
    });
  };

  trackSoftCTA = this.createLinkAnalyticsHandler('soft-cta');

  renderSoftCTA = () => {
    const showCTA = get(this.fields, 'ShowSoftCTA.value');
    if (showCTA) {
      const CTAField = this.getViewAllCTAField();
      return (
        <CtaLink
          cta={CTAField}
          arrow={arrows.right}
          css={styles.softCTA}
          onClick={this.trackSoftCTA}
        />
      );
    }
  };

  trackHardCTA = this.createLinkAnalyticsHandler('hard-cta');

  renderHardCta = () => {
    const showCTA = get(this.fields, 'ShowHardSellCTA.value');
    if (showCTA) {
      const CTAField = this.getViewAllCTAField();
      return (
        <LinkWrapper
          css={styles.hardCTA}
          field={CTAField}
          onClick={this.trackHardCTA}
        />
      );
    }
  };

  renderMainContent = () => {
    const { fields, wildcardValues } = this;

    const SolutionsHeadline = {
      value: transformWildcardsToText({
        ...wildcardValues,
        text: get(fields, 'SolutionsHeadline.value', ''),
      }),
    };

    const BodyText = {
      value: transformWildcardsToText({
        ...wildcardValues,
        text: get(fields, 'BodyText.value', ''),
      }),
    };

    return (
      <React.Fragment>
        <div css={styles.headlineCol}>
          <h2 css={styles.headline}>
            <Text field={SolutionsHeadline} />
            <DotComponent />
          </h2>
        </div>
        {this.renderSoftCTA()}
        <p css={styles.bodyText}>
          <Text field={BodyText} />
        </p>
      </React.Fragment>
    );
  };

  safeRender() {
    const { props, fields } = this;
    const hasStickyLabel = componentHasStickyLabel(props);
    const {
      Images,
      QuantityOfLocations,
      QuantityOfOfficesToRent,
      QuantityOfCoworkingDesks,
      QuantityOfMeetingRooms,
    } = fields;

    return (
      <Spacer
        marginCss={styles.containerMargin}
        containerProps={{
          'data-sticky-id': hasStickyLabel && get(props, 'rendering.uid'),
        }}
      >
        <Container>
          <Row>
            <Column
              css={getshowStyles(MediaQueries.mdAndUp, 'inline-block')}
              cols={{ sm: 0, md: 4, lg: 4 }}
              offsets={{ lg: 1 }}
            >
              {Images.value.length > 0 && (
                <GalleryCarousel
                  images={Images.value}
                  imageSizes={imageSizes}
                />
              )}
            </Column>
            <Column cols={{ sm: 6, md: 4, lg: 5 }} offsets={{ lg: 1 }}>
              {this.renderMainContent()}
              <Row css={styles.statsContainer}>
                {QuantityOfLocations &&
                  !isEmpty(QuantityOfLocations) &&
                  this.renderStat(
                    QuantityOfLocations,
                    SolutionsInTheAreaKeys.LocationsLabel,
                  )}
                {QuantityOfOfficesToRent &&
                  !isEmpty(QuantityOfOfficesToRent) &&
                  this.renderStat(
                    QuantityOfOfficesToRent,
                    SolutionsInTheAreaKeys.OfficesToRentLabel,
                  )}
                {QuantityOfCoworkingDesks &&
                  !isEmpty(QuantityOfCoworkingDesks) &&
                  this.renderStat(
                    QuantityOfCoworkingDesks,
                    SolutionsInTheAreaKeys.CoworkingDeskLabel,
                  )}
                {QuantityOfMeetingRooms &&
                  !isEmpty(QuantityOfMeetingRooms) &&
                  this.renderStat(
                    QuantityOfMeetingRooms,
                    SolutionsInTheAreaKeys.MeetingRoomLabel,
                  )}
              </Row>
              <Row>
                <Column
                  cols={{ sm: [6, 6], md: [4, 4], lg: [4, 5], xl: [3, 5] }}
                >
                  {this.renderHardCta()}
                </Column>
              </Row>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

SolutionsInTheArea.displayName = 'SolutionsInTheArea';

SolutionsInTheArea.defaultProps = { fields: defaultData, t: key => key };

SolutionsInTheArea.propTypes = {
  fields: PropTypes.shape({
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
    BodyText: PropTypes.shape({
      value: PropTypes.string,
    }),
    QuantityOfLocations: PropTypes.shape({
      value: PropTypes.number,
    }),
    QuantityOfOfficesToRent: PropTypes.shape({
      value: PropTypes.number,
    }),
    QuantityOfCoworkingDesks: PropTypes.shape({
      value: PropTypes.number,
    }),
    QuantityOfOffices: PropTypes.shape({
      value: PropTypes.number,
    }),
    QuantityOfLocations: PropTypes.shape({
      value: PropTypes.number,
    }),
    Images: PropTypes.shape({
      value: PropTypes.arrayOf(
        PropTypes.shape({
          Image: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
            caption: PropTypes.string,
          }),
        }),
      ),
    }),
  }),
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(SolutionsInTheArea);
